/**
 * Adult Content Context
 * Manages the "Hide 18+ Content" setting.
 * Default: hidden (true). Users can toggle in Settings.
 * Persisted to localStorage and server settings.
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import { getSettingFromServer, saveSettingToServer } from '../services/apiService';
import { waitForServerAuth } from '../services/authReadyService';

const AdultContentContext = createContext();

const STORAGE_KEY = 'hide_adult_content';
const SERVER_KEY = 'hide_adult_content';

const isUserConnected = () => {
    const { hasToken } = require('../services/tokenStorage');
    return hasToken();
};

export const AdultContentProvider = ({ children }) => {
    const [hideAdultContent, setHideAdultContent] = useState(() => {
        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            // Default to true (hide adult content)
            return stored !== null ? JSON.parse(stored) : true;
        } catch {
            return true;
        }
    });

    // Sync from server on mount (waits for server auth to complete first)
    useEffect(() => {
        const loadFromServer = async () => {
            if (isUserConnected()) {
                try {
                    await waitForServerAuth(); // Wait for server login to complete
                    const serverValue = await getSettingFromServer(SERVER_KEY);
                    if (serverValue !== null) {
                        setHideAdultContent(serverValue);
                        localStorage.setItem(STORAGE_KEY, JSON.stringify(serverValue));
                    }
                } catch (error) {
                    console.error('Error loading adult content setting:', error);
                }
            }
        };
        loadFromServer();
    }, []);

    const toggleHideAdultContent = () => {
        setHideAdultContent(prev => {
            const newValue = !prev;
            localStorage.setItem(STORAGE_KEY, JSON.stringify(newValue));
            if (isUserConnected()) {
                saveSettingToServer(SERVER_KEY, newValue).catch(err =>
                    console.warn('Server sync failed for adult content setting:', err.message)
                );
            }
            return newValue;
        });
    };

    return (
        <AdultContentContext.Provider value={{ hideAdultContent, toggleHideAdultContent }}>
            {children}
        </AdultContentContext.Provider>
    );
};

export const useAdultContent = () => {
    const context = useContext(AdultContentContext);
    if (!context) {
        throw new Error('useAdultContent must be used within AdultContentProvider');
    }
    return context;
};

export default AdultContentContext;
